/**
 * Lead detail presentational sections (WP27).
 */

import type { HotelDetail } from "@carago/shared";
import { Link } from "react-router-dom";
import { formatBoolTri, formatDateTime } from "../lib/hotels.js";

export type HotelDetailViewState = "loading" | "error" | "ready";

export type HotelDetailViewProps = {
  state: HotelDetailViewState;
  errorMessage?: string | null;
  detail: HotelDetail | null;
  noteContent: string;
  taskTitle: string;
  taskDueAt: string;
  actionMessage?: string | null;
  actionError?: string | null;
  submittingNote?: boolean;
  submittingTask?: boolean;
  onNoteContentChange: (value: string) => void;
  onTaskTitleChange: (value: string) => void;
  onTaskDueAtChange: (value: string) => void;
  onSubmitNote: () => void;
  onSubmitTask: () => void;
};

function formatClaimValue(value: unknown): string {
  if (value == null) return "—";
  if (typeof value === "string") return value;
  if (typeof value === "boolean") return value ? "ja" : "nein";
  if (typeof value === "number") return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

export function HotelDetailView({
  state,
  errorMessage,
  detail,
  noteContent,
  taskTitle,
  taskDueAt,
  actionMessage,
  actionError,
  submittingNote,
  submittingTask,
  onNoteContentChange,
  onTaskTitleChange,
  onTaskDueAtChange,
  onSubmitNote,
  onSubmitTask,
}: HotelDetailViewProps) {
  if (state === "loading") {
    return (
      <p data-testid="hotel-detail-loading" className="text-sm text-gray-500">
        Lade Lead…
      </p>
    );
  }

  if (state === "error" || !detail) {
    return (
      <div
        data-testid="hotel-detail-error"
        className="rounded-lg border border-red-100 bg-red-100/40 px-4 py-3 text-sm text-red-800"
      >
        {errorMessage ?? "Lead konnte nicht geladen werden."}
        <div className="mt-3">
          <Link to="/hotels" className="font-medium underline">
            Zurück zur Pipeline
          </Link>
        </div>
      </div>
    );
  }

  const { hotel, claims, contacts, drafts } = detail;

  return (
    <div className="mx-auto max-w-5xl space-y-6" data-testid="hotel-detail-ready">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold tracking-wide text-gray-400 uppercase">
            {hotel.pipeline_status}
          </p>
          <h2 className="text-2xl font-semibold text-gray-800">{hotel.name}</h2>
          <p className="text-sm text-gray-500">
            {[hotel.city, hotel.country].filter(Boolean).join(", ") || "—"}
          </p>
          {hotel.website ? (
            <a
              href={hotel.website}
              target="_blank"
              rel="noreferrer"
              className="mt-1 inline-block text-sm text-violet-700 hover:underline"
            >
              {hotel.website}
            </a>
          ) : null}
        </div>
        <Link
          to="/hotels"
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Zur Pipeline
        </Link>
      </div>

      <dl className="grid gap-4 rounded-xl border border-gray-200 bg-white p-5 sm:grid-cols-3">
        <Detail term="Lead Score" value={hotel.lead_score ?? "—"} />
        <Detail term="Priorität" value={hotel.priority ?? "—"} />
        <Detail term="Shuttle" value={formatBoolTri(hotel.has_shuttle)} />
        <Detail
          term="Airport Transfer"
          value={formatBoolTri(hotel.has_airport_transfer)}
        />
        <Detail
          term="Letzte Recherche"
          value={formatDateTime(hotel.last_researched_at)}
        />
        <Detail term="Nächste Aktion" value={hotel.next_action ?? "—"} />
        <Detail
          term="Fällig"
          value={formatDateTime(hotel.next_action_due_at)}
        />
        <Detail
          term="Scoring"
          value={hotel.scoring_ruleset_version ?? "—"}
        />
        <Detail
          term="Do not contact"
          value={hotel.do_not_contact ? "Ja" : "Nein"}
        />
      </dl>

      {hotel.scoring_rationale ? (
        <section className="rounded-xl border border-gray-200 bg-white p-5">
          <h3 className="mb-2 text-sm font-semibold text-gray-800">
            Scoring-Rationale
          </h3>
          <p className="text-sm text-gray-600">{hotel.scoring_rationale}</p>
        </section>
      ) : null}

      <section className="rounded-xl border border-gray-200 bg-white p-5">
        <h3 className="mb-3 text-sm font-semibold text-gray-800">
          Claims & Sources
        </h3>
        {claims.length === 0 ? (
          <p className="text-sm text-gray-500">Keine Claims vorhanden.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
              <thead className="bg-gray-50 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                <tr>
                  <th className="px-3 py-2">Field</th>
                  <th className="px-3 py-2">Value</th>
                  <th className="px-3 py-2">Confidence</th>
                  <th className="px-3 py-2">Source</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {claims.map((claim) => (
                  <tr key={claim.id}>
                    <td className="px-3 py-2 font-medium text-gray-800">
                      {claim.field}
                    </td>
                    <td className="px-3 py-2 text-gray-700">
                      {formatClaimValue(claim.value_json)}
                    </td>
                    <td className="px-3 py-2 text-gray-600">
                      {claim.confidence.toFixed(2)}
                    </td>
                    <td className="px-3 py-2">
                      <a
                        href={claim.source_url}
                        target="_blank"
                        rel="noreferrer"
                        className="break-all text-violet-700 hover:underline"
                      >
                        {claim.source_url}
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-5">
        <h3 className="mb-3 text-sm font-semibold text-gray-800">Contacts</h3>
        {contacts.length === 0 ? (
          <p className="text-sm text-gray-500">Keine Kontakte vorhanden.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {contacts.map((contact) => (
              <li key={contact.id} className="py-3 text-sm">
                <div className="font-medium text-gray-800">
                  {contact.name}
                  {contact.is_primary ? (
                    <span className="ml-2 text-xs text-violet-700">primary</span>
                  ) : null}
                </div>
                <div className="text-gray-600">
                  {contact.role_title ?? "—"} · {contact.email ?? "keine E-Mail"} ·{" "}
                  {contact.email_status}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-5">
        <h3 className="mb-3 text-sm font-semibold text-gray-800">
          Email Drafts
        </h3>
        {drafts.length === 0 ? (
          <p className="text-sm text-gray-500">Keine Drafts vorhanden.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {drafts.map((draft) => (
              <li key={draft.id} className="py-3 text-sm">
                <div className="font-medium text-gray-800">{draft.subject}</div>
                <div className="text-gray-600">
                  {draft.purpose} · {draft.status} ·{" "}
                  {formatDateTime(draft.updated_at)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="grid gap-4 md:grid-cols-2">
        <form
          className="space-y-3 rounded-xl border border-gray-200 bg-white p-5"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmitNote();
          }}
        >
          <h3 className="text-sm font-semibold text-gray-800">Notiz hinzufügen</h3>
          <textarea
            data-testid="hotel-note-input"
            className="min-h-24 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 outline-none focus:border-violet-500"
            value={noteContent}
            onChange={(e) => onNoteContentChange(e.target.value)}
            placeholder="Interne Notiz…"
            required
          />
          <button
            type="submit"
            disabled={submittingNote}
            className="rounded-lg bg-violet-600 px-3 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50"
          >
            {submittingNote ? "Speichere…" : "Notiz speichern"}
          </button>
        </form>

        <form
          className="space-y-3 rounded-xl border border-gray-200 bg-white p-5"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmitTask();
          }}
        >
          <h3 className="text-sm font-semibold text-gray-800">Task erstellen</h3>
          <input
            data-testid="hotel-task-title"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 outline-none focus:border-violet-500"
            value={taskTitle}
            onChange={(e) => onTaskTitleChange(e.target.value)}
            placeholder="Titel"
            required
          />
          <input
            type="datetime-local"
            data-testid="hotel-task-due"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 outline-none focus:border-violet-500"
            value={taskDueAt}
            onChange={(e) => onTaskDueAtChange(e.target.value)}
          />
          <button
            type="submit"
            disabled={submittingTask}
            className="rounded-lg bg-violet-600 px-3 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50"
          >
            {submittingTask ? "Erstelle…" : "Task erstellen"}
          </button>
        </form>
      </section>

      {actionMessage ? (
        <p className="text-sm text-green-800" data-testid="hotel-action-ok">
          {actionMessage}
        </p>
      ) : null}
      {actionError ? (
        <p className="text-sm text-red-800" data-testid="hotel-action-error">
          {actionError}
        </p>
      ) : null}
    </div>
  );
}

function Detail({
  term,
  value,
}: {
  term: string;
  value: string | number;
}) {
  return (
    <div>
      <dt className="text-xs font-semibold tracking-wide text-gray-400 uppercase">
        {term}
      </dt>
      <dd className="mt-1 text-sm text-gray-800">{value}</dd>
    </div>
  );
}
